import Key from "csi/core/Key";
import Spec from "csi/heading/Spec";
import { organizeComponentItems } from "csi/middleware/common";
import CustomViewAttributesStore from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import { DndItemWithId } from "designSystem/dnd";
import { BLANK_COMPONENTITEM, ComponentItem } from "designSystem/types/component-item-types";
import { action, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";
import { DropTargetMonitor } from "react-dnd";
import { ICustomViewSort } from "src/c8-platform/type/customView";

// dnd attribute types for drag and drop
export enum DND_TYPE {
	SORTING_LEVEL = "SORTING_LEVEL",
}

export class SortingLevel implements DndItemWithId {
	@observable.ref item: ComponentItem = BLANK_COMPONENTITEM;

	/** indicates whether the attribute is sorted in ascending order or descending order */
	@observable descending: boolean = false;

	/** indicates the type of the item (ref, reflist, boolean, string) */
	@observable valueType: string;

	/** unique id */
	id: string;

	@action setSortingLevelSortOrder = (descending: boolean): void => {
		this.descending = descending;
	};

	constructor() {
		makeObservable(this);
		this.id = `sortingLevel_${new Date().getTime()}`;
	}
}
export default class CustomViewSortingStore {
	readonly attributeStore: CustomViewAttributesStore;

	sortingList: SortingLevel[] = observable.array([], { deep: false });

	/** this record stores the component items that we exclude from showing in the dropdown because the attributes were already selected */
	excludingOptionsList = observable.map<string, ComponentItem>(null, { deep: false });

	/** array that stores the selected attributes in the current sort settings */
	existingSortSettingSpecs: Spec[] = [];

	@action addSortingLevel = (): void  => {
		this.sortingList.push(new SortingLevel());
	};

	@action	removeSortingLevel = (index: number): void => {
		this.excludingOptionsList.delete(this.sortingList[index].item.id);
		this.sortingList.splice(index, 1);
	};

	@action setSortingLevel = (item: ComponentItem, index: number): void => {
		const { excludingOptionsList, sortingList, specs } = this;
		excludingOptionsList.set(item.id, item);
		if (sortingList[index].item.id !== "") {
			excludingOptionsList.delete(sortingList[index].item.id);
		}
		sortingList[index].item = item;

		// Specs could be a long list. Will come back to change it if affecting performance or when final design is certain. Creating a map of <name, type> could be a potential solution
		specs.some((value) => {
			if (value.name === item.name) {
				sortingList[index].valueType = value.type;
				return true;
			}
		});
	};

	get overLimit(): boolean {
		return this.sortingList.length > 3;
	}

	/** drop item dnd */
	@action dropItem = (item: SortingLevel, monitor: DropTargetMonitor, targetIndex: number, groupId: string, list: SortingLevel[]): void => {
		this.sortingList.splice(0, this.sortingList.length);
		list.forEach((sortingLevel) => this.sortingList.push(sortingLevel));
	};

	/** return the specs from attributes in the current view and the attributes selected in sort setting */
	get specs(): Spec[] {
		return this.attributeStore.attributeSpecsInView.concat(this.existingSortSettingSpecs);
	}

	/** return the current sort settings */
	get currentSortSettings(): ICustomViewSort[] {
		return this.sortingList.map((level) => ({
			descending: level.descending,
			key: level.item.id, 
		  }));
	}

	/** the available options based on selected type */
	getOptions = (item: ComponentItem): ComponentItem[] => {
		const { convertSpecToComponentItemForCustomView } = this.attributeStore;

		// get the specs that can sort and are not already selected
		let sortingOptions = this.specs
			.filter((spec) => spec.canSort() && !this.excludingOptionsList.has(spec.toString()))
			.map((spec) => convertSpecToComponentItemForCustomView(spec));
		
		// include the item selected in the dropdown
		if (item.id != "") {
			sortingOptions = sortingOptions.concat(item);
		}
		return organizeComponentItems(sortingOptions);
	};

	constructor(sortSettings: ICustomViewSort[], attributeStore: CustomViewAttributesStore, existingSortSettingSpecs: Spec[]) {
		makeObservable(this);
		this.attributeStore = attributeStore;
		this.existingSortSettingSpecs = existingSortSettingSpecs;
	
		sortSettings?.forEach((value, index) => {
			this.addSortingLevel();
			this.sortingList[index].setSortingLevelSortOrder(value.descending);
			const key = attributeStore.selectedLevel.specBuilder.getSpecForKey(value.key);
			this.setSortingLevel(attributeStore.convertSpecToComponentItemForCustomView(key), index);
		});
	}
}

export const CustomViewSortingStoreContext = createContext<CustomViewSortingStore>(undefined);
export const useCustomViewSortingStore = (): CustomViewSortingStore => useContext(CustomViewSortingStoreContext);
